import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Plus, Pencil, Trash2, Truck, Save, X, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyProvider = {
  name: '',
  contact: '',
  notes: '',
  tariffs: [{ qty: 1, price_eur_no_vat: 0 }],
  shipping_per_pack_eur: 6,
  max_books_per_pack: 6,
  avg_book_weight_g: 300,
  margin_pct: 40,
  is_active: true,
  is_default: false,
};

/**
 * Admin CRUD for print providers (imprentas + tarifas + envío).
 * Each provider holds a quantity → price table so the operator can update
 * the printer's quote without touching code.
 */
export const PrintProvidersDialog = ({ open, onClose, onChanged }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // provider object or 'new' marker
  const [saving, setSaving] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/print-providers`, { withCredentials: true });
      setProviders(res.data?.providers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const startCreate = () => setEditing({ ...emptyProvider, _new: true });
  const startEdit = (p) => setEditing({ ...p });

  const submit = async () => {
    const payload = {
      name: editing.name?.trim(),
      contact: editing.contact || '',
      notes: editing.notes || '',
      tariffs: (editing.tariffs || [])
        .filter((t) => t.qty && t.price_eur_no_vat !== '')
        .map((t) => ({ qty: parseInt(t.qty, 10), price_eur_no_vat: parseFloat(t.price_eur_no_vat) })),
      shipping_per_pack_eur: parseFloat(editing.shipping_per_pack_eur) || 0,
      max_books_per_pack: parseInt(editing.max_books_per_pack, 10) || 6,
      avg_book_weight_g: parseInt(editing.avg_book_weight_g, 10) || 300,
      margin_pct: parseFloat(editing.margin_pct) || 0,
      is_active: !!editing.is_active,
      is_default: !!editing.is_default,
    };
    if (!payload.name) {
      toast.error('El nombre del proveedor es obligatorio');
      return;
    }
    if (payload.tariffs.length === 0) {
      toast.error('Define al menos una tarifa válida');
      return;
    }
    setSaving(true);
    try {
      if (editing._new) {
        await axios.post(`${API_URL}/api/admin/print-providers`, payload, { withCredentials: true });
        toast.success('Proveedor creado');
      } else {
        await axios.put(
          `${API_URL}/api/admin/print-providers/${editing.provider_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Proveedor actualizado');
      }
      setEditing(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const p = deleteCandidate;
    if (!p) return;
    try {
      await axios.delete(`${API_URL}/api/admin/print-providers/${p.provider_id}`, { withCredentials: true });
      toast.success(`"${p.name}" eliminado`);
      setDeleteCandidate(null);
      refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const setTariffRow = (idx, field, value) => {
    const next = [...(editing.tariffs || [])];
    next[idx] = { ...next[idx], [field]: value };
    setEditing({ ...editing, tariffs: next });
  };
  const addTariffRow = () => {
    const next = [...(editing.tariffs || [])];
    const lastQty = next.length ? Math.max(...next.map((t) => parseInt(t.qty, 10) || 0)) : 0;
    next.push({ qty: lastQty + 1, price_eur_no_vat: 0 });
    setEditing({ ...editing, tariffs: next });
  };
  const removeTariffRow = (idx) => {
    const next = [...(editing.tariffs || [])];
    next.splice(idx, 1);
    setEditing({ ...editing, tariffs: next });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="print-providers-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b bg-muted/30">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Truck className="w-5 h-5 text-teal-600" />
              Proveedores de imprenta
            </DialogTitle>
            <DialogDescription>
              Gestiona las imprentas con las que trabajas. Cada una tiene su propia tabla de tarifas por cantidad y reglas de envío.
            </DialogDescription>
          </DialogHeader>

          {!editing ? (
            <>
              <div className="px-5 py-3 border-b flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {providers.length} {providers.length === 1 ? 'proveedor' : 'proveedores'}
                </p>
                <Button size="sm" onClick={startCreate} className="gap-1" data-testid="new-provider-btn">
                  <Plus className="w-3.5 h-3.5" /> Nuevo proveedor
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {loading ? (
                  <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                ) : providers.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-12 italic">Aún no hay ningún proveedor.</p>
                ) : (
                  providers.map((p) => (
                    <div
                      key={p.provider_id}
                      className="border rounded-lg p-4 hover:bg-muted/40 transition-colors"
                      data-testid={`provider-card-${p.provider_id}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{p.name}</p>
                            {p.is_default && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 text-[10px]">
                                <Star className="w-3 h-3" /> Por defecto
                              </Badge>
                            )}
                            {!p.is_active && (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactivo</Badge>
                            )}
                          </div>
                          {p.contact && <p className="text-xs text-muted-foreground mt-0.5">{p.contact}</p>}
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                            <div><span className="text-muted-foreground">Tarifas:</span> {p.tariffs?.length || 0} filas</div>
                            <div><span className="text-muted-foreground">Margen:</span> {p.margin_pct}%</div>
                            <div><span className="text-muted-foreground">Envío:</span> €{p.shipping_per_pack_eur}/pack</div>
                            <div><span className="text-muted-foreground">Pack:</span> {p.max_books_per_pack} libros</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(p)} className="gap-1 text-xs" data-testid={`edit-provider-${p.provider_id}`}>
                            <Pencil className="w-3.5 h-3.5" /> Editar
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setDeleteCandidate(p)}
                            className="gap-1 text-xs text-red-600 hover:text-red-700"
                            data-testid={`delete-provider-${p.provider_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nombre *</Label>
                    <Input
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      placeholder="p. ej. Imprenta Madrid"
                      data-testid="provider-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contacto / Email / URL</Label>
                    <Input
                      value={editing.contact || ''}
                      onChange={(e) => setEditing({ ...editing, contact: e.target.value })}
                      placeholder="comercial@imprenta.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notas internas</Label>
                  <Textarea
                    rows={2}
                    value={editing.notes || ''}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                    placeholder="Condiciones de pago, plazos, observaciones…"
                  />
                </div>

                <div className="rounded-md border p-3 bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium">Tabla de tarifas (sin IVA)</p>
                    <Button size="sm" variant="ghost" onClick={addTariffRow} className="gap-1 text-xs h-7" data-testid="add-tariff-row">
                      <Plus className="w-3 h-3" /> Añadir fila
                    </Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-left">
                      <tr>
                        <th className="py-1 w-24">Cantidad</th>
                        <th className="py-1">Precio (€)</th>
                        <th className="py-1 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editing.tariffs || []).map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="py-1 pr-2">
                            <Input
                              type="number" min={1}
                              value={row.qty}
                              onChange={(e) => setTariffRow(idx, 'qty', e.target.value)}
                              className="h-8 text-xs"
                              data-testid={`tariff-qty-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <Input
                              type="number" min={0} step="0.01"
                              value={row.price_eur_no_vat}
                              onChange={(e) => setTariffRow(idx, 'price_eur_no_vat', e.target.value)}
                              className="h-8 text-xs"
                              data-testid={`tariff-price-${idx}`}
                            />
                          </td>
                          <td className="py-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => removeTariffRow(idx)}
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                              disabled={editing.tariffs.length <= 1}
                              data-testid={`remove-tariff-${idx}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Margen aplicado (%)</Label>
                    <Input
                      type="number" min={0} step="0.1"
                      value={editing.margin_pct}
                      onChange={(e) => setEditing({ ...editing, margin_pct: e.target.value })}
                      data-testid="provider-margin-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Envío por pack (€)</Label>
                    <Input
                      type="number" min={0} step="0.1"
                      value={editing.shipping_per_pack_eur}
                      onChange={(e) => setEditing({ ...editing, shipping_per_pack_eur: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Libros por pack</Label>
                    <Input
                      type="number" min={1}
                      value={editing.max_books_per_pack}
                      onChange={(e) => setEditing({ ...editing, max_books_per_pack: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Peso libro (g)</Label>
                    <Input
                      type="number" min={1}
                      value={editing.avg_book_weight_g}
                      onChange={(e) => setEditing({ ...editing, avg_book_weight_g: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6 flex-wrap pt-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch
                      checked={editing.is_active}
                      onCheckedChange={(c) => setEditing({ ...editing, is_active: c })}
                    />
                    Activo
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch
                      checked={editing.is_default}
                      onCheckedChange={(c) => setEditing({ ...editing, is_default: c })}
                    />
                    Por defecto
                  </label>
                </div>
              </div>

              <DialogFooter className="px-5 py-3 border-t bg-muted/30">
                <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>
                  Cancelar
                </Button>
                <Button onClick={submit} disabled={saving} className="gap-1" data-testid="save-provider-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {editing._new ? 'Crear' : 'Guardar cambios'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar proveedor</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar "{deleteCandidate?.name}"? Si algún libro está usando este proveedor, la operación se bloqueará.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PrintProvidersDialog;
